import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import type { AssemblyTemplateQuery } from '@/types';
import type { FilterField, FilterPill } from './InsightsSearchBar.types';
import { SPATIAL_DECKS, SPATIAL_PAINT, SPATIAL_WORKPACK } from './constants';

/** Regex to detect range expressions like ">50", "<=100", "=10-20" */
const RANGE_EXPRESSION_PATTERN = /[><=!]|=\s*\d+(\.\d+)?\s*-\s*\d+(\.\d+)?/;

type SetActiveFieldId = (update: string | null | ((prev: string | null) => string | null)) => void;

interface UseSearchBarHandlersParams {
  assemblyTemplate: AssemblyTemplateQuery | undefined;
  activeFieldId: string | null;
  setActiveFieldId: SetActiveFieldId;
  addTemplateOption: (fieldKey: string, value: string) => void;
  addDeckFilter: (id: string) => void;
  addPaintRegionFilter: (id: string) => void;
  addWorkpackFilter: (id: string) => void;
  setApplyFilters: (value: boolean) => void;
  setSearchTerm: (term: string | ((prev: string) => string)) => void;
  setRangeSearchTerm: (term: string) => void;
  removePill: (pill: FilterPill) => void;
}

export function useSearchBarHandlers({
  assemblyTemplate,
  activeFieldId,
  setActiveFieldId,
  addTemplateOption,
  addDeckFilter,
  addPaintRegionFilter,
  addWorkpackFilter,
  setApplyFilters,
  setSearchTerm,
  setRangeSearchTerm,
  removePill,
}: UseSearchBarHandlersParams) {
  const setRefetchEquipmentTable = useSetAtom(analysisState.refetchEquipmentTable);

  const triggerTableRefetch = useCallback(() => {
    setRefetchEquipmentTable((count) => (count ?? 0) + 1);
  }, [setRefetchEquipmentTable]);

  const closeDropdown = useCallback(
    (
      setActiveFieldName: (name: string | null) => void,
      setInputValue: (value: string) => void,
      setOpen: (open: boolean) => void
    ) => {
      setActiveFieldId(null);
      setActiveFieldName(null);
      setInputValue('');
      setOpen(false);
    },
    [setActiveFieldId]
  );

  const handleShowFilterOptions = useCallback(
    (
      fieldId: string,
      fieldName: string,
      setActiveFieldName: (name: string | null) => void,
      setInputValue: (value: string) => void,
      setOpen: (open: boolean) => void
    ) => {
      setActiveFieldId((prev) => (prev === fieldId ? null : fieldId));
      setActiveFieldName(fieldName);
      setInputValue('');
      setOpen(true);
    },
    [setActiveFieldId]
  );

  const handleSelectOption = useCallback(
    (activeField: FilterField | undefined, optionId: string, closeDropdownFn: () => void) => {
      if (!activeField) return;

      if (activeField.type === 'template') {
        const field = assemblyTemplate?.fields?.find((f) => f.id === activeFieldId);
        if (field) {
          const fieldKey = field.key ?? field.id;
          addTemplateOption(fieldKey, optionId);
        }
      } else if (activeField.id === SPATIAL_DECKS) {
        addDeckFilter(optionId);
      } else if (activeField.id === SPATIAL_PAINT) {
        addPaintRegionFilter(optionId);
      } else if (activeField.id === SPATIAL_WORKPACK) {
        addWorkpackFilter(optionId);
      }

      closeDropdownFn();
      setApplyFilters(true);
    },
    [
      activeFieldId,
      assemblyTemplate,
      addTemplateOption,
      addDeckFilter,
      addPaintRegionFilter,
      addWorkpackFilter,
      setApplyFilters,
    ]
  );

  const handleSearchSubmit = useCallback(
    (value: string, closeDropdownFn: () => void) => {
      if (RANGE_EXPRESSION_PATTERN.test(value)) {
        setRangeSearchTerm(value);
      } else {
        setSearchTerm(value);
      }
      closeDropdownFn();
      triggerTableRefetch();
    },
    [setSearchTerm, setRangeSearchTerm, triggerTableRefetch]
  );

  const handleSaveEditedPill = useCallback(
    (pill: FilterPill, editedValue: string, closeDropdownFn: () => void) => {
      const colonIndex = editedValue.indexOf(':');
      const hasFieldPrefix = colonIndex >= 0;
      const filterValue = hasFieldPrefix
        ? editedValue.slice(colonIndex + 1).trim()
        : editedValue.trim();

      removePill(pill);

      switch (pill.type) {
        case 'template': {
          addTemplateOption(pill.fieldKey, filterValue);
          break;
        }
        case 'searchTerm': {
          setSearchTerm((prev) => {
            const terms = (prev ?? '')
              .split('||')
              .map((t) => t.trim())
              .filter(Boolean);
            const index = terms.indexOf(pill.value);
            if (index === -1) return filterValue;
            terms[index] = filterValue;
            return terms.join('||');
          });
          triggerTableRefetch();
          break;
        }
        case 'rangeSearchTerm': {
          setRangeSearchTerm(filterValue);
          break;
        }
      }

      setApplyFilters(true);
      closeDropdownFn();
    },
    [
      removePill,
      addTemplateOption,
      setSearchTerm,
      setRangeSearchTerm,
      setApplyFilters,
      triggerTableRefetch,
    ]
  );

  return {
    closeDropdown,
    handleShowFilterOptions,
    handleSelectOption,
    handleSearchSubmit,
    handleSaveEditedPill,
    triggerTableRefetch,
  };
}
